import { DefaultOptionType } from "antd5/es/cascader";
import qs from "qs";

import { encodeCompressed } from "lib/hooks/useURLState";
import { routes } from "lib/routes";
import { EMPTY_CONTACT_FILTERS as EMPTY_UNIFIED_CONTACT_FILTERS } from "./cognism/cognismContactFilters";
import { ContactFilters } from "./contactUtils";
import { ContactFiltersComplete } from "./FilterForm";

export const SENIORITY_OPTIONS: DefaultOptionType[] = [
  {
    label: "Chairman",
    value: "Chairman",
  },
  {
    label: "Chief Officer",
    value: "Chief Officer",
  },
  {
    label: "Clerk/Secretary",
    value: "Clerk/Secretary",
  },
  {
    label: "Deputy/Assistant Chief Officer",
    value: "Deputy/Assistant Chief Officer",
  },
  {
    label: "Second Level / Head of Service",
    value: "Second Level / Head of Service",
  },
  {
    label: "Third Level/Head of Service/Manager",
    value: "Third Level/Head of Service/Manager",
  },
  {
    label: "Top Level Director",
    value: "Top Level Director",
  },
  {
    label: "Unspecified",
    value: "Unspecified",
  },
  {
    label: "Vice/Deputy Chairman",
    value: "Vice/Deputy Chairman",
  },
];

export const JOB_FUNCTION_OPTIONS: DefaultOptionType[] = [
  {
    label: "Admin & Support",
    value: "Admin & Support",
  },
  {
    label: "Educator",
    value: "Educator",
  },
  {
    label: "Finance",
    value: "Finance",
  },
  {
    label: "Health Services",
    value: "Health Services",
  },
  {
    label: "HR & Training",
    value: "HR & Training",
  },
  {
    label: "IT & Digital",
    value: "IT & Digital",
  },
  {
    label: "Legal & Compliance",
    value: "Legal & Compliance",
  },
  {
    label: "Marketing, Communications, & Sales",
    value: "Marketing, Communications, & Sales",
  },
  {
    label: "Operations",
    value: "Operations",
  },
  {
    label: "Procurement",
    value: "Procurement",
  },
  {
    label: "Service Delivery",
    value: "Service Delivery",
  },
  {
    label: "Uncategorised",
    value: "Uncategorised",
  },
];

export const EMPTY_CONTACT_FILTERS: ContactFiltersComplete & {
  sort: { field: string; order: "ASC" | "DESC" };
} = {
  sort: {
    field: "job_title",
    order: "ASC",
  },
  jobTitle: "",
  firstName: "",
  lastName: "",
  seniority: [],
  jobFunction: [],
  buyerGuids: [],
  buyerListIds: [],
  buyerCategoryIds: [],
  includeUncategorisedBuyers: false,
  contactDisplayOptions: [],
  contactDisplayOptionsEmail: false,
  contactDisplayOptionsPhone: false,
};

/**
 * Buyer scope in the unified contacts table's own filter shape, compressed the way that
 * table's `useURLState` expects to read it. Only the buyer filters translate between the
 * two shapes: the legacy role filters hold display labels (`seniority`, `jobFunction`) and
 * a single `jobTitle` string, where the unified ones hold tier/function keys and a keyword
 * array. Returns undefined when there is no buyer scope to seed, so an unscoped link stays
 * a bare /contacts.
 */
function encodeUnifiedBuyerScope(filters: Partial<ContactFilters>): string | undefined {
  const buyerScope = {
    buyerGuids: filters.buyerGuids ?? [],
    buyerListIds: filters.buyerListIds ?? [],
    buyerCategoryIds: filters.buyerCategoryIds ?? [],
  };
  if (!Object.values(buyerScope).some((ids) => ids.length > 0)) {
    return undefined;
  }
  const encoded = encodeCompressed({ ...EMPTY_UNIFIED_CONTACT_FILTERS, ...buyerScope });
  // encodeCompressed hands back the input object if compression fails; there is nothing
  // useful to put in the URL in that case.
  return typeof encoded === "string" ? encoded : undefined;
}

/**
 * Links to the contacts page with filters pre-applied.
 *
 * /contacts serves one of two tables depending on the COGNISM_CONTACTS flag, and they read
 * different query params: the legacy table reads `contactsFilters` (plain qs), the unified
 * one reads `contactFilters` (compressed, its own schema). Both are emitted so a single
 * link pre-filters whichever table the viewer gets; each table ignores the other's key.
 */
export function generateContactsUrl(filters: Partial<ContactFilters>): string {
  const params = qs.stringify(
    { contactsFilters: filters, contactFilters: encodeUnifiedBuyerScope(filters) },
    { arrayFormat: "brackets", addQueryPrefix: true },
  );
  return `${routes.contacts}${params}`;
}
